import { useState } from "react";
import type { StepView } from "@/app/queries";
import { CheckRow } from "@/ui/CheckRow";
import { MARK_COLOR, gateResources, metaLine, resourceProgress, type ResourceProgress, type TickFn } from "./model";

/** Ресурсы шага (S4, 4a): карточка с долей юнитов, раскрытие — таблица
 *  юнитов гайда с чекбоксами; ресурс без гайда отмечается целиком.
 *  Необязательные ресурсы — в «Первоисточнике»; ⏭️-юниты серые, отметить
 *  можно, но в долю и счётчик гейта они не входят. */
export function Resources({ view, tick }: { view: StepView; tick: TickFn }) {
  const g = view.gate;
  return (
    <section id="res" className="scroll-mt-20">
      <div className="mb-3 flex items-baseline gap-2.5">
        <h2 className="m-0 font-mono text-[15px] font-semibold">Ресурсы</h2>
        <span className="font-mono text-[11.5px] text-mut" data-testid="units-summary">
          {g.units_done} / {g.units_total} юнитов отмечено
        </span>
      </div>
      <div className="flex flex-col gap-3">
        {gateResources(view).map((r) => (
          <ResourceCard key={r.id} p={resourceProgress(r, view.ticks)} ticks={view.ticks} tick={tick} />
        ))}
      </div>
    </section>
  );
}

const UNIT_GRID = "grid grid-cols-[30px_minmax(0,1.1fr)_minmax(0,1.3fr)_74px_26px] items-center gap-3 px-3.5";

function ResourceCard({ p, ticks, tick }: { p: ResourceProgress; ticks: StepView["ticks"]; tick: TickFn }) {
  // Раскрыт по умолчанию тот ресурс, где есть что доделать прямо сейчас:
  // начатый гайд или неотмеченный ресурс без гайда.
  const [open, setOpen] = useState(p.noGuide ? !p.wholeTicked : p.done > 0 && p.done < p.total);
  const ticked = new Set(ticks.units);
  const pct = p.total > 0 ? Math.round((100 * p.done) / p.total) : 0;
  const r = p.res;
  return (
    <div className="overflow-hidden rounded-md border border-bd bg-card" data-testid="resource">
      <div className="grid grid-cols-[22px_minmax(0,1fr)_128px_20px] items-center gap-3 px-3.5 py-3">
        <span aria-hidden className="font-mono text-[12px]" style={{ color: MARK_COLOR[p.mark] }}>
          {p.mark}
        </span>
        <div className="min-w-0">
          <div className="text-[14px] font-semibold leading-[1.3]">
            {r.url ? (
              <a href={r.url} target="_blank" rel="noreferrer" className="text-ink no-underline hover:text-acc">
                {r.title}
              </a>
            ) : (
              r.title
            )}
          </div>
          <div className="mt-1 font-mono text-[11.5px] text-mut">{metaLine(r)}</div>
        </div>
        <div>
          <div className="mb-[5px] h-1 overflow-hidden rounded-[2px] bg-card2" role="progressbar" aria-valuenow={p.done} aria-valuemax={p.total} aria-label={r.title}>
            <div className="h-full bg-acc" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-right font-mono text-[10.5px] text-mut">{p.frac}</div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`${open ? "свернуть" : "развернуть"} «${r.title}»`}
          className="cursor-pointer border-0 bg-transparent p-0 font-mono text-[13px] text-mut hover:text-ink"
        >
          {open ? "−" : "+"}
        </button>
      </div>
      {open && (
        <div className="border-t border-bd bg-bg2">
          {p.noGuide ? (
            <div className="px-3.5 py-2">
              <div className="py-1.5 font-mono text-[11.5px] text-mut">гайд по ресурсу не размечен — отмечай ресурс целиком</div>
              <div id={`resource-${r.id}`}>
                <CheckRow checked={p.wholeTicked} onChange={(on) => tick({ kind: "resource", key: r.id, on })} label="отметить ресурс целиком" />
              </div>
            </div>
          ) : (
            <>
              <div className={`${UNIT_GRID} border-b border-bd py-2 font-mono text-[10.5px] uppercase tracking-[.05em] text-mut`} aria-hidden>
                <span>#</span>
                <span>тема</span>
                <span>ключевое понятие</span>
                <span>гайд</span>
                <span />
              </div>
              {r.units.map((u, i) => {
                const on = ticked.has(u.key);
                const skip = Boolean(u.skip);
                return (
                  <label
                    key={u.key}
                    id={`unit-${u.key}`}
                    data-testid={skip ? "unit-skip" : "unit"}
                    data-skip={skip}
                    className={`${UNIT_GRID} cursor-pointer border-b border-bd py-[9px] last:border-b-0`}
                  >
                    <span className="font-mono text-[11.5px] text-mut">{i + 1}</span>
                    <span className="min-w-0">
                      <span className={`block text-[13px] ${on || skip ? "text-mut" : "text-ink"}`}>{u.topic}</span>
                      {skip && <span className="mt-0.5 block font-mono text-[10.5px] text-mut">можно пропустить · не входит в гейт</span>}
                    </span>
                    <span className="font-mono text-[11.5px] text-mut">{u.concept}</span>
                    {skip ? (
                      <span className="font-mono text-[11px]" title="можно пропустить">
                        ⏭️
                      </span>
                    ) : (
                      <span className="font-mono text-[11px] text-ok">✅</span>
                    )}
                    <input type="checkbox" checked={on} onChange={(e) => tick({ kind: "unit", key: u.key, on: e.target.checked })} aria-label={u.topic} className="size-[15px] cursor-pointer accent-[var(--acc)]" />
                  </label>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
